import { useState } from "react";
import type { DraftFeedback } from "@/lib/api/apps/types";

const NONE: DraftFeedback = { tables: {}, note: "" };
const storageKey = (appId: string) => `design-marks:${appId}`;

function read(appId: string, n: number): DraftFeedback | null {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(appId)) ?? "null");
    return saved?.n === n
      ? { tables: saved.tables ?? {}, note: saved.note ?? "" }
      : null;
  } catch {
    return null;
  }
}

function write(appId: string, n: number, marks: DraftFeedback) {
  try {
    if (Object.keys(marks.tables).length === 0 && !marks.note)
      localStorage.removeItem(storageKey(appId));
    else
      localStorage.setItem(storageKey(appId), JSON.stringify({ n, ...marks }));
  } catch {
    // Storage off: the marks last as long as the page.
  }
}

/** The marks on version `n` not yet sent, kept in this browser so leaving the
 *  page loses nothing; a new version starts from what it carried, else clean. */
export function useUnsentMarks(
  appId: string,
  n: number | undefined,
  carried?: DraftFeedback | null,
) {
  const [held, setHeld] = useState<{ n?: number; marks: DraftFeedback }>({
    marks: NONE,
  });
  if (n !== undefined && held.n !== n)
    setHeld({ n, marks: read(appId, n) ?? carried ?? NONE });
  const marks = held.n === n ? held.marks : NONE;
  const update = (next: DraftFeedback) => {
    if (n === undefined) return;
    setHeld({ n, marks: next });
    write(appId, n, next);
  };
  return [marks, update] as const;
}
